import { Link, useParams } from 'react-router-dom';
import { useGetStockInReceiptQuery, useListVendorsQuery } from '../store/apiSlice.js';
import { Loader, ErrorAlert } from '../components/States.jsx';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { SHOP } from '../config/shop.js';
import { fmtDate } from '../utils/format.js';

export default function StockInInvoicePage() {
  const { id } = useParams();
  const { data: receipt, isLoading, error, refetch } = useGetStockInReceiptQuery(id);
  const { data: vendors } = useListVendorsQuery({});

  if (isLoading) {
    return (
      <>
        <PageHeader eyebrow="Stock In" title="Goods Receipt" />
        <Card><Loader label="Loading receipt..." /></Card>
      </>
    );
  }
  if (error) {
    return (
      <>
        <PageHeader eyebrow="Stock In" title="Goods Receipt" />
        <Card><ErrorAlert message={error.message} onRetry={refetch} /></Card>
      </>
    );
  }

  const vendor = (vendors || []).find((row) => row.company === receipt.vendor) || {};

  return (
    <>
      <div className="no-print">
        <PageHeader
          eyebrow="Stock In"
          title={receipt.receiptNo}
          subtitle={`${receipt.vendor} · ${fmtDate(receipt.date)}`}
        />
        <div className="btn-row" style={{ marginBottom: 18 }}>
          <button type="button" className="btn primary" onClick={() => window.print()}>
            Print / Save as PDF
          </button>
          <Link className="btn" to="/stock-in" style={{ textDecoration: 'none' }}>
            Back to stock in
          </Link>
          {receipt.updatedAt && (
            <span className="muted" style={{ fontSize: 11.5, alignSelf: 'center' }}>
              Corrected on {fmtDate(receipt.updatedAt)}
            </span>
          )}
        </div>
      </div>

      <article className="invoice-sheet">
        <header className="invoice-head">
          <div>
            <div className="invoice-shop">{SHOP.name}</div>
            <div className="invoice-tagline">{SHOP.tagline}</div>
            <div className="invoice-lines">
              {SHOP.addressLines.map((line) => (
                <div key={line}>{line}</div>
              ))}
              <div>GSTIN: {SHOP.gstin}</div>
              <div>
                {SHOP.phone} · {SHOP.email}
              </div>
            </div>
          </div>
          <div className="invoice-title-block">
            <div className="invoice-title">Goods Receipt</div>
            <table className="invoice-meta">
              <tbody>
                <tr>
                  <th>Receipt No</th>
                  <td>{receipt.receiptNo}</td>
                </tr>
                <tr>
                  <th>Date</th>
                  <td>{fmtDate(receipt.date)}</td>
                </tr>
                <tr>
                  <th>Total Units</th>
                  <td>{receipt.totalQty}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </header>

        <section className="invoice-party">
          <div>
            <div className="invoice-party-label">Received From</div>
            <div className="invoice-party-name">{receipt.vendor}</div>
            <div className="invoice-lines">
              {vendor.contact && <div>{vendor.contact}</div>}
              {vendor.address && <div>{vendor.address}</div>}
              {vendor.phone && <div>{vendor.phone}</div>}
              <div>GSTIN: {vendor.gst || 'Unregistered'}</div>
            </div>
          </div>
          <div>
            <div className="invoice-party-label">Document Type</div>
            <div className="invoice-party-name">Stock In</div>
          </div>
        </section>

        <table className="invoice-table">
          <thead>
            <tr>
              <th className="sr-col">#</th>
              <th>Description</th>
              <th className="num">Previous</th>
              <th className="num">Received</th>
              <th className="num">Total Stock</th>
            </tr>
          </thead>
          <tbody>
            {receipt.rows.map((row, index) => (
              <tr key={row.id}>
                <td className="sr-col">{index + 1}</td>
                <td>
                  <strong>{row.product}</strong>
                  <div className="invoice-sku">
                    {row.texture} · {row.length} · {row.color}
                  </div>
                </td>
                <td className="num">{row.previousStock ?? '-'}</td>
                <td className="num">{row.qty}</td>
                <td className="num">{row.totalStock ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <section className="invoice-foot">
          <div className="invoice-words">
            <div className="invoice-party-label">Remarks</div>
            <div className="invoice-words-text">
              Goods received into inventory. Stock balances are calculated at save time from the full ledger.
            </div>
          </div>

          <table className="invoice-totals">
            <tbody>
              <tr>
                <th>Line Items</th>
                <td>{receipt.rows.length}</td>
              </tr>
              <tr className="invoice-grand">
                <th>Total Units</th>
                <td>{receipt.totalQty}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <footer className="invoice-terms">
          <div>
            <div className="invoice-party-label">Checked By</div>
            <div className="invoice-lines">Inventory team</div>
          </div>
          <div className="invoice-sign">
            <div>For {SHOP.name}</div>
            <div className="invoice-sign-line" />
            <div>Authorised Signatory</div>
          </div>
        </footer>
      </article>
    </>
  );
}
